import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Briefcase } from "lucide-react";

const FORMATIONS = [
  {
    group: "BBA — Bachelor of Business Administration",
    color: "bg-navy-500",
    items: [
      {
        num: "01", to: "/formations/bba/1", label: "BBA 1ère année", level: "Bac+1",
        access: "Post-bac",
        desc: "Fondamentaux du management, marketing, communication et outils digitaux. Premier stage de découverte en entreprise.",
        icon: GraduationCap,
      },
      {
        num: "02", to: "/formations/bba/2", label: "BBA 2ème année", level: "Bac+2",
        access: "BBA1 validé ou admission parallèle Bac+1/Bac+2",
        desc: "Management avancé, stratégie d'entreprise, marketing digital. Mission tutorée sur un cas réel.",
        icon: GraduationCap,
      },
      {
        num: "03", to: "/formations/bba/3", label: "BBA 3ème année", level: "Bac+3",
        access: "BBA2 validé ou admission parallèle Bac+2/Bac+3",
        desc: "Spécialisation parmi 7 options (marketing digital, finance, RH, entrepreneuriat…). Possibilité de double diplôme.",
        icon: GraduationCap,
      },
    ],
  },
  {
    group: "MBA — Master of Business Administration",
    color: "bg-red-500",
    items: [
      {
        num: "04", to: "/formations/mba/1", label: "MBA 1ère année", level: "Bac+4",
        access: "Bac+3 validé",
        desc: "Tronc commun stratégique : management des équipes, finance d'entreprise, business international et conduite du changement.",
        icon: Briefcase,
      },
      {
        num: "05", to: "/formations/mba/2", label: "MBA 2ème année", level: "Bac+5",
        access: "MBA1 validé ou admission parallèle Bac+4",
        desc: "Spécialisation au choix : Marketing & Digital, Finance, RH, Entrepreneuriat, Management International… Mémoire professionnel.",
        icon: Briefcase,
      },
      {
        num: "06", to: "/formations/mba-executive", label: "MBA Executive", level: "Bac+5+",
        access: "Bac+5 + 5 ans d'expérience minimum",
        desc: "Pour les cadres et dirigeants en activité, format week-ends sur 11 mois.",
        icon: Briefcase,
      },
    ],
  },
];

const Card = ({ item, index }) => {
  const [hovered, setHovered] = useState(false);
  const Icon = item.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ y: -8 }}
      className="relative bg-white border border-navy-100 overflow-hidden cursor-pointer isolate rounded-none"
      style={{
        boxShadow: hovered
          ? "0 30px 70px -20px rgba(0,35,98,0.30), 0 0 0 1px rgba(237,41,57,0.10)"
          : "0 8px 28px -16px rgba(0,35,98,0.12)",
      }}
    >
      {/* Animated background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br from-navy-500 via-navy-700 to-[#000e27] origin-bottom transition-transform duration-[700ms] ease-[cubic-bezier(0.65,0,0.35,1)] -z-10 ${hovered ? "scale-y-100" : "scale-y-0"}`} />

      {/* Dot pattern */}
      <svg className={`absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-700 ${hovered ? "opacity-[0.08]" : "opacity-0"}`} preserveAspectRatio="none">
        <defs>
          <pattern id={`fo-pat-${item.num}`} width="16" height="16" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="white" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#fo-pat-${item.num})`} />
      </svg>

      {/* Glow orb */}
      <div className={`absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl transition-opacity duration-700 ${hovered ? "opacity-100 bg-red-500/30" : "opacity-0"}`} />

      {/* Giant background number */}
      <span className={`absolute -top-2 right-4 font-display text-[160px] leading-none select-none pointer-events-none transition-all duration-500 ${hovered ? "text-white/[0.08] scale-110" : "text-navy-700/[0.06] scale-100"}`} aria-hidden>
        {item.num}
      </span>

      <Link to={item.to} className="relative flex flex-col p-8 md:p-10 min-h-[340px]">
        {/* Icon */}
        <motion.div
          animate={{ rotate: hovered ? -8 : -4, scale: hovered ? 1.05 : 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-14 h-14 mb-6"
        >
          <div className={`absolute inset-0 transition-colors duration-500 ${hovered ? "bg-red-500" : "bg-navy-50"}`} style={{ borderRadius: "8px" }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className={`h-6 w-6 transition-colors duration-500 ${hovered ? "text-white" : "text-navy-500"}`} strokeWidth={1.5} />
          </div>
        </motion.div>

        {/* Level badge */}
        <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold mb-2 transition-colors duration-500 ${hovered ? "text-red-400" : "text-red-500"}`}>
          {item.level} — {item.access}
        </div>

        {/* Title */}
        <h3 className={`font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors duration-500 ${hovered ? "text-white" : "text-navy-700"}`}>
          {item.label}
        </h3>

        {/* Desc */}
        <p className={`text-[14px] leading-relaxed flex-1 transition-colors duration-500 ${hovered ? "text-white/80" : "text-[#555]"}`}>
          {item.desc}
        </p>

        {/* CTA */}
        <div className={`mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors duration-500 ${hovered ? "text-white" : "text-navy-700"}`}>
          Voir le programme
          <ArrowRight className={`h-3.5 w-3.5 transition-transform duration-300 ${hovered ? "translate-x-1" : ""}`} />
        </div>
      </Link>

      {/* Tricolor stripe */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] flex overflow-hidden">
        <motion.span animate={{ scaleX: hovered ? 1 : 0 }} transition={{ duration: 0.5, delay: hovered ? 0 : 0 }} className="flex-1 bg-[#002362] origin-left" />
        <motion.span animate={{ scaleX: hovered ? 1 : 0 }} transition={{ duration: 0.5, delay: hovered ? 0.1 : 0 }} className="flex-1 bg-white origin-left" />
        <motion.span animate={{ scaleX: hovered ? 1 : 0 }} transition={{ duration: 0.5, delay: hovered ? 0.2 : 0 }} className="flex-1 bg-[#ED2939] origin-left" />
      </div>
    </motion.div>
  );
};

export const FormationsOverview = () => {
  return (
    <section id="formations-overview" className="section-padding bg-[#FAF8F3]">
      <div className="container-edge">

        <div className="max-w-3xl mb-14">
          <div className="overline mb-4">Nos programmes</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-5xl text-navy-700 leading-tight">
            Du <em className="not-italic red-shine">post-bac</em> au Bac+5
          </h2>
          <p className="text-[#555] mt-5 text-[16px] leading-relaxed max-w-2xl">
            Cinq ans pour décrocher le diplôme ECL Lyon. Deux cycles pour y parvenir. Une liberté totale sur votre niveau d'entrée et votre rythme. Ce n'est pas un parcours standardisé, c'est le vôtre.
          </p>
        </div>

        <div className="space-y-10">
          {FORMATIONS.map((group) => (
            <div key={group.group}>
              <div className="flex items-center gap-3 mb-6">
                <span className={`${group.color} text-white text-[10px] uppercase tracking-[0.18em] font-semibold px-3 py-1.5`}>
                  {group.group}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {group.items.map((item, i) => (
                  <Card key={item.to} item={item} index={i} />
                ))}
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default FormationsOverview;
